import { useState } from "react";
import { Star, PenLine, Send, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import ShareButtons from "@/components/ShareButtons";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function ReviewForm({ services = [] }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ author: "", service: "", rating: 5, text: "", website: "" });
  const [hover, setHover] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [thanked, setThanked] = useState(false);

  const closeAll = (v) => {
    setOpen(v);
    if (!v) setThanked(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.author.trim() || !form.text.trim()) {
      toast.error("Please add your name and a comment.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/reviews", {
        author: form.author,
        service: form.service,
        rating: form.rating,
        text: form.text,
        website: form.website,
      });
      toast.success(data.message || "Thanks for your review!");
      const happy = form.rating >= 4;
      setForm({ author: "", service: "", rating: 5, text: "", website: "" });
      if (happy) setThanked(true);
      else setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={closeAll}>
      <DialogTrigger asChild>
        <button
          className="group inline-flex items-center gap-2 border border-[#d4af37] px-7 py-3 overline text-xs text-[#d4af37] hover:bg-[#d4af37] hover:text-black transition-colors duration-300"
          data-testid="open-review-form-btn"
        >
          <PenLine className="w-4 h-4" strokeWidth={1.5} /> Leave a Review
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#0a0a0a] border border-[#d4af37]/30 text-white rounded-none max-w-lg" data-testid="review-form-dialog">
        {thanked ? (
          <div className="text-center py-6" data-testid="review-thanks">
            <div className="w-16 h-16 border-2 border-[#d4af37] rounded-full flex items-center justify-center mx-auto mb-6">
              <PartyPopper className="w-8 h-8 text-[#d4af37]" strokeWidth={1.5} />
            </div>
            <h3 className="font-display text-2xl text-white mb-2">Thank you!</h3>
            <p className="text-white/50 font-body text-sm mb-8 max-w-sm mx-auto">Your review means the world. It&apos;ll appear once Reek approves it. Mind sharing the shop with a friend?</p>
            <ShareButtons text="I get my cuts at The Barbers Chair 💈 Highly recommend — book online:" label="" />
            <button onClick={() => closeAll(false)} className="mt-8 border border-white/20 px-8 py-3 overline text-xs text-white/70 hover:text-white transition-colors" data-testid="review-thanks-done">Done</button>
          </div>
        ) : (
        <>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-white">Share your experience</DialogTitle>
          <DialogDescription className="text-white/50 font-body">Tell others about your visit — reviews appear once approved.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-6 pt-2">
          {/* Honeypot: hidden from users, bots tend to fill it */}
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            value={form.website}
            onChange={(e) => setForm({ ...form, website: e.target.value })}
            style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
          />
          <div>
            <label className="overline text-[0.6rem] text-white/50 block mb-2">Your Name *</label>
            <input
              value={form.author}
              onChange={(e) => setForm({ ...form, author: e.target.value })}
              className="w-full bg-transparent border-b border-white/20 py-3 text-white font-body focus:border-[#d4af37] focus:outline-none transition-colors"
              data-testid="review-name"
            />
          </div>

          <div>
            <label className="overline text-[0.6rem] text-white/50 block mb-2">Service</label>
            <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
              <SelectTrigger className="w-full bg-transparent border-white/20 text-white rounded-none" data-testid="review-service">
                <SelectValue placeholder="Which service?" />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0a0a] border-white/15 text-white max-h-64">
                {services.map((s) => (
                  <SelectItem key={s.id} value={s.name} className="text-white text-sm focus:bg-[#d4af37]/20 focus:text-white">{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="overline text-[0.6rem] text-white/50 block mb-3">Rating</label>
            <div className="flex gap-2" data-testid="review-rating">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onMouseEnter={() => setHover(n)}
                  onMouseLeave={() => setHover(0)}
                  onClick={() => setForm({ ...form, rating: n })}
                  data-testid={`review-star-${n}`}
                >
                  <Star className={`w-7 h-7 transition-colors ${n <= (hover || form.rating) ? "fill-[#d4af37] text-[#d4af37]" : "text-white/25"}`} />
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="overline text-[0.6rem] text-white/50 block mb-2">Comment *</label>
            <textarea
              rows={4}
              value={form.text}
              onChange={(e) => setForm({ ...form, text: e.target.value })}
              className="w-full bg-transparent border border-white/20 p-3 text-white font-body text-sm focus:border-[#d4af37] focus:outline-none transition-colors resize-none"
              placeholder="Tell others about your cut…"
              data-testid="review-text"
            />
          </div>

          <DialogFooter>
            <button
              type="submit"
              disabled={submitting}
              className="group w-full inline-flex items-center justify-center gap-2 bg-[#d4af37] text-black py-3.5 overline text-xs hover:bg-[#c5a028] disabled:opacity-50 transition-colors"
              data-testid="submit-review-btn"
            >
              {submitting ? "Submitting…" : "Submit Review"} <Send className="w-4 h-4" strokeWidth={2} />
            </button>
          </DialogFooter>
          <p className="text-white/30 text-xs font-body text-center">Reviews appear after the shop approves them.</p>
        </form>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
}
